import React, { useEffect, useState } from 'react';
import './StreakDisplay.css';

interface StreakDisplayProps {
  streak: number;
  lastActivity: Date | null | string;
}

const StreakDisplay: React.FC<StreakDisplayProps> = ({ streak, lastActivity }) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (streak > 0) {
      setShowAnimation(true);
      setTimeout(() => setShowAnimation(false), 1000);
    }
  }, [streak]);

  const getStreakMessage = () => {
    if (streak === 0) return "Start your streak today!";
    if (streak < 3) return "Good start! Keep going!";
    if (streak < 7) return "You're on fire! 🔥";
    if (streak < 30) return "Amazing dedication!";
    if (streak < 100) return "Unstoppable force!";
    return "Legendary cleaner! 👑";
  };

  const getMilestoneReward = () => {
    const milestones = [3, 7, 14, 30, 60, 100, 150, 200, 250, 300, 365];
    const nextMilestone = milestones.find(m => m > streak);
    if (!nextMilestone) return null;
    
    const daysToNext = nextMilestone - streak;
    return {
      days: daysToNext,
      milestone: nextMilestone,
      reward: nextMilestone === 3 ? "Early Bird" :
              nextMilestone === 7 ? "Week Warrior" :
              nextMilestone === 14 ? "Fortnight Fighter" :
              nextMilestone === 30 ? "Monthly Master" :
              nextMilestone === 60 ? "Commitment King" :
              nextMilestone === 100 ? "Century Champion" :
              nextMilestone === 150 ? "Unstoppable Force" :
              nextMilestone === 200 ? "Lightning Legend" :
              nextMilestone === 250 ? "Space Explorer" :
              nextMilestone === 300 ? "Cosmic Cleaner" :
              "Year Champion"
    };
  };

  const milestone = getMilestoneReward();

  return (
    <div className={`streak-display ${showAnimation ? 'animate' : ''}`}>
      <div className="streak-main">
        <div className="streak-icon">
          {streak === 0 ? '⭐' : '🔥'}
        </div>
        <div className="streak-info">
          <h3 className="streak-number">{streak}</h3>
          <p className="streak-label">Login Streak</p>
        </div>
      </div>
      
      <p className="streak-message">{getStreakMessage()}</p>
      
      {milestone && (
        <div className="milestone-progress">
          <div className="milestone-info">
            <span className="milestone-text">
              {milestone.days} days to {milestone.reward}
            </span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((milestone.milestone - milestone.days) / milestone.milestone) * 100}%` }}
            />
          </div>
        </div>
      )}

      {streak > 0 && (
        <div className="streak-badges">
          <div className={`badge ${streak >= 3 ? 'earned' : 'locked'}`}>
            <span className="badge-icon">🥉</span>
            <span className="badge-label">3 Days</span>
          </div>
          <div className={`badge ${streak >= 7 ? 'earned' : 'locked'}`}>
            <span className="badge-icon">🥈</span>
            <span className="badge-label">7 Days</span>
          </div>
          <div className={`badge ${streak >= 30 ? 'earned' : 'locked'}`}>
            <span className="badge-icon">🥇</span>
            <span className="badge-label">30 Days</span>
          </div>
          <div className={`badge ${streak >= 100 ? 'earned' : 'locked'}`}>
            <span className="badge-icon">💎</span>
            <span className="badge-label">100 Days</span>
          </div>
          <div className={`badge ${streak >= 365 ? 'earned' : 'locked'}`}>
            <span className="badge-icon">🏆</span>
            <span className="badge-label">365 Days</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreakDisplay;